import type { SelectOption } from "../components/ReusableComponents/Select";

interface DatasetOptionSource {
  id: string;
  name: string;
  slug?: string | null;
  datasetType?: string | null;
  units?: string | null;
}

/** First 8 hex chars of the dataset's UUID, e.g. "hist-ross-…-6380b864-1510-…" → "6380b864"; falls back to the id's head. */
export const shortDatasetId = (id: string): string =>
  id.match(/([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[1] ?? id.slice(0, 8);

const toOption = (dataset: DatasetOptionSource, group: string): SelectOption => ({
  value: dataset.id,
  label: `${dataset.name}${dataset.datasetType ? ` (${dataset.datasetType})` : ""}${dataset.units ? ` — ${dataset.units}` : ""}`,
  // Search matches the description too, so the slug and short id are findable as well as the name
  description: [dataset.slug, shortDatasetId(dataset.id)].filter(Boolean).join(" · "),
  group,
});

/** Picker options for a dataset select: name on the first line, slug · short id under it, personal then organization. */
export const buildDatasetSelectOptions = (
  personal: DatasetOptionSource[],
  organization: DatasetOptionSource[]
): SelectOption[] => {
  const byName = (a: DatasetOptionSource, b: DatasetOptionSource) => a.name.localeCompare(b.name);
  const personalIds = new Set(personal.map((d) => d.id));
  return [
    ...[...personal].sort(byName).map((d) => toOption(d, "My datasets")),
    // A dataset the user owns that also sits in their org is listed once, under My datasets
    ...organization.filter((d) => !personalIds.has(d.id)).sort(byName).map((d) => toOption(d, "Organization")),
  ];
};
